// src/services/api.js
const API_URL =
  process.env.REACT_APP_API_URL || "http://localhost:5000/api";

/**
 * ✅ One browser tab/session = one stable tracking token
 * This prevents early API calls from creating multiple session tokens.
 */
const getStableSessionToken = () => {
  let token = localStorage.getItem("esill-session");

  if (!token) {
    token = sessionStorage.getItem("esill-session-temp");
  }

  if (!token) {
    token = crypto.randomUUID();
  }

  localStorage.setItem("esill-session", token);
  sessionStorage.setItem("esill-session-temp", token);
  window.__SESSION_TOKEN__ = token;

  return token;
};

const getReferrerDomain = (url) => {
  try {
    if (!url) return "";

    const parsed = new URL(url);

    // ✅ Ignore same-site referrers
    if (parsed.hostname === window.location.hostname) {
      return "";
    }

    return parsed.hostname.replace(/^www\./, "");
  } catch {
    return "";
  }
};

const getTrafficSource = () => {
  const storageKey = "esill-traffic-source";

  const existing = localStorage.getItem(storageKey);

  const params = new URLSearchParams(window.location.search);

  const utmSource = params.get("utm_source") || "";
  const utmMedium = params.get("utm_medium") || "";
  const utmCampaign = params.get("utm_campaign") || "";

  const rawReferrer = document.referrer || "";
  const referrerDomain = getReferrerDomain(rawReferrer);

  // ✅ If UTM exists, always refresh attribution
  // Example: ?utm_source=instagram&utm_medium=social&utm_campaign=spring
  if (utmSource || utmMedium || utmCampaign) {
    const data = {
      referrerUrl: rawReferrer,
      referrerDomain: referrerDomain || utmSource,
      utmSource,
      utmMedium,
      utmCampaign,
    };

    localStorage.setItem(storageKey, JSON.stringify(data));
    return data;
  }

  // ✅ Keep first valid external referrer for this browser
  if (existing) {
    try {
      return JSON.parse(existing);
    } catch {
      localStorage.removeItem(storageKey);
    }
  }

  const data = {
    referrerUrl: rawReferrer,
    referrerDomain,
    utmSource: "",
    utmMedium: "",
    utmCampaign: "",
  };

  localStorage.setItem(storageKey, JSON.stringify(data));
  return data;
};

export async function apiRequest(path, options = {}) {
  const token = localStorage.getItem("esill-token");
  const sessionToken = getStableSessionToken();
  const trafficSource = getTrafficSource();

  const res = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(token && { Authorization: `Bearer ${token}` }),
      ...(sessionToken && { "x-session-token": sessionToken }),

      // ✅ Referrer / campaign tracking
      "x-referrer-url": trafficSource.referrerUrl || "",
      "x-referrer-domain": trafficSource.referrerDomain || "",
      "x-utm-source": trafficSource.utmSource || "",
      "x-utm-medium": trafficSource.utmMedium || "",
      "x-utm-campaign": trafficSource.utmCampaign || "",

      ...(options.headers || {}),
    },
  });

  const data = await res.json();

  if (!res.ok) {
    const err = new Error(data.message || "API error");
    err.status = res.status;
    err.path = path;
    throw err;
  }

  return data;
}